import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { STRINGS } from "@/lib/i18n";
import { waLink } from "@/lib/wa";
import { Brand, PoweredBy } from "@/components/Brand";
import { toast } from "sonner";
import { QrCode, Wallet, IndianRupee, Gift, ChevronLeft, CheckCircle2, Search, Loader2, MapPin, ShieldCheck } from "lucide-react";

const CROWN = "#8B0000";

export default function ScanJourney() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState("en");
  const t = (k) => STRINGS[lang][k] || k;
  const [step, setStep] = useState("code"); // code, reward, mobile, register, dealer, upi, gift, done
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [rewardType, setRewardType] = useState("wallet");
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("painter");
  const [existing, setExisting] = useState(null);
  const [dealerQ, setDealerQ] = useState("");
  const [dealers, setDealers] = useState([]);
  const [dealer, setDealer] = useState(null);
  const [result, setResult] = useState(null);
  // upi
  const [upiId, setUpiId] = useState(""); const [holder, setHolder] = useState(""); const [consent, setConsent] = useState(false);
  const [upiResult, setUpiResult] = useState(null);
  // gift
  const [gifts, setGifts] = useState([]); const [gift, setGift] = useState(null); const [pin, setPin] = useState(""); const [address, setAddress] = useState("");

  const doValidate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/journey/validate", { code });
      toast.dismiss();
      setCoupon(data); setStep("reward");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current) return;
    const qc = searchParams.get("code");
    autoRan.current = true;
    setBusy(true);
    api.get("/public/whatsapp-number")
      .then((r) => { if (r.data.number) window.location.replace(waLink(r.data.number, qc ? qc.toUpperCase() : "")); else setBusy(false); })
      .catch(() => setBusy(false));
    // eslint-disable-next-line
  }, []);


  const chooseReward = (r) => { setRewardType(r); setStep("mobile"); };

  const doIdentify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/journey/identify", { mobile });
      if (data.existing) { setExisting(data.user); setName(data.user.name); setRole(data.user.role); }
      else setExisting(null);
      await loadDealers(""); setStep("dealer");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const loadDealers = async (q) => {
    try {
      const { data } = await api.get("/dealers/search", { params: { text: q || "paint", city: q, limit: 12 } });
      setDealers(data.dealers || []);
    } catch { setDealers([]); }
  };

  const doRedeem = async () => {
    if (!dealer) { toast.error("Please select a dealer"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/journey/redeem", {
        code, mobile, name: name || existing?.name, role, dealer_id: dealer.id, reward_type: rewardType,
      });
      setResult(data);
      if (rewardType === "wallet") setStep("done");
      else if (rewardType === "upi") { setHolder(name || existing?.name || ""); setStep("upi"); }
      else { const g = await api.get("/gifts"); setGifts(g.data.filter((x) => x.active)); setStep("gift"); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doUpi = async () => {
    if (!consent) { toast.error("Consent required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/upi/redeem", {
        user_id: result.user_id, points: result.wallet_balance, upi_id: upiId, holder, consent,
      });
      setUpiResult(data); setStep("done");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doGiftOrder = async () => {
    if (!gift) { toast.error("Select a gift"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/order", { user_id: result.user_id, gift_id: gift.id, address, pin });
      setUpiResult({ gift: true, order: data }); setStep("done");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const back = () => {
    const order = ["code", "reward", "mobile", "dealer", "upi", "gift", "done"];
    const i = order.indexOf(step);
    if (i <= 0) nav("/"); else setStep(order[Math.max(0, i - 1)]);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center">
      <div className="w-full max-w-md min-h-screen bg-white shadow-xl flex flex-col border-x border-gray-100">
        <header className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <button onClick={back} data-testid="scan-back-btn" className="p-1.5 -ml-1.5 rounded-lg hover:bg-gray-100"><ChevronLeft className="h-5 w-5 text-gray-600" /></button>
          <Brand compact />
          <button data-testid="language-toggle-btn" onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="text-xs font-bold border border-gray-300 rounded-full px-3 py-1 hover:border-[#8B0000] hover:text-[#8B0000]">
            {lang === "en" ? "हिंदी" : "ENG"}
          </button>
        </header>

        <div className="flex-1 p-6 flex flex-col">
          {step === "code" && (
            <div className="animate-slide-up flex-1 flex flex-col">
              <div className="mx-auto mt-6 mb-8 h-40 w-40 rounded-3xl bg-[#FDF2F2] flex items-center justify-center border-2 border-dashed border-[#8B0000]/30">
                <QrCode className="h-20 w-20 text-[#8B0000]" strokeWidth={1.4} />
              </div>
              <h1 className="font-display text-2xl font-extrabold text-gray-900 text-center">{t("scan_title")}</h1>
              <p className="text-gray-500 text-center mt-2 text-sm">{t("scan_sub")}</p>
              <input data-testid="qr-scan-input-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t("coupon_placeholder")}
                className="mt-8 w-full px-4 py-4 text-center font-mono text-lg tracking-wider border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#8B0000] focus:border-[#8B0000] outline-none" />
              <button data-testid="qr-validate-btn" disabled={!code || busy} onClick={doValidate}
                className="mt-auto w-full bg-[#8B0000] hover:bg-[#6B0000] text-white font-extrabold text-lg py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-50">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />} {t("validate")}
              </button>
            </div>
          )}

          {step === "reward" && coupon && (
            <div className="animate-slide-up flex-1 flex flex-col">
              <div className="bg-gradient-to-br from-red-50 to-white p-5 rounded-2xl border-2 border-dashed border-[#8B0000]/30 text-center">
                <ShieldCheck className="h-8 w-8 text-green-600 mx-auto" />
                <p className="mt-2 text-xs uppercase tracking-wider text-gray-500 font-semibold">{t("reward_ready")}</p>
                <p className="mt-1 font-semibold text-gray-800">{coupon.product_name}</p>
                <div className="mt-2 kpi-num text-5xl text-[#8B0000]">{coupon.reward_points}</div>
                <p className="text-sm text-gray-500">{coupon.point_unit}</p>
              </div>
              <h2 className="mt-8 font-display text-lg font-bold text-gray-900 text-center">{t("choose_reward")}</h2>
              <div className="mt-4 space-y-3">
                {[["wallet", Wallet, t("wallet")], ["upi", IndianRupee, t("upi")], ["gift", Gift, t("gift")]].map(([k, Icon, label]) => (
                  <button key={k} data-testid={`reward-choice-${k}-button`} onClick={() => chooseReward(k)}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-[#8B0000] hover:bg-[#FDF2F2] transition-all text-left">
                    <div className="h-11 w-11 rounded-lg bg-[#FDF2F2] flex items-center justify-center text-[#8B0000]"><Icon className="h-5 w-5" /></div>
                    <span className="font-bold text-gray-900">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === "mobile" && (
            <div className="animate-slide-up flex-1 flex flex-col">
              <h2 className="font-display text-2xl font-bold text-gray-900">{t("mobile_title")}</h2>
              <p className="text-gray-500 mt-1 text-sm">{t("mobile_sub")}</p>
              <div className="mt-6 flex items-center border-2 border-gray-300 rounded-xl overflow-hidden focus-within:border-[#8B0000]">
                <span className="px-3 py-4 bg-gray-50 text-gray-500 font-semibold border-r">+91</span>
                <input data-testid="journey-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder={t("mobile_placeholder")} className="flex-1 px-4 py-4 text-lg outline-none" inputMode="numeric" />
              </div>
              <button data-testid="journey-mobile-continue" disabled={mobile.length !== 10 || busy} onClick={doIdentify}
                className="mt-auto w-full bg-[#8B0000] hover:bg-[#6B0000] text-white font-extrabold text-lg py-4 rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />} {t("continue")}
              </button>
            </div>
          )}

          {step === "dealer" && (
            <div className="animate-slide-up flex-1 flex flex-col">
              {!existing ? (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">{t("your_name")}</label>
                    <input data-testid="journey-name-input" value={name} onChange={(e) => setName(e.target.value)}
                      className="mt-1 w-full px-4 py-3 border-2 border-gray-300 rounded-xl outline-none focus:border-[#8B0000]" placeholder={t("your_name")} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">{t("select_role")}</label>
                    <div className="mt-1 grid grid-cols-3 gap-2">
                      {[["painter", t("painter")], ["contractor", t("contractor")], ["dealer", t("dealer")]].map(([k, l]) => (
                        <button key={k} data-testid={`role-${k}`} onClick={() => setRole(k)}
                          className={`py-2.5 rounded-lg text-sm font-semibold border-2 ${role === k ? "border-[#8B0000] bg-[#FDF2F2] text-[#8B0000]" : "border-gray-200 text-gray-600"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-800">
                  Welcome back, <b>{existing.name}</b>! {existing.last_dealer_name ? `Last dealer: ${existing.last_dealer_name}` : ""}
                </div>
              )}
              <h2 className="font-display text-lg font-bold text-gray-900">{t("choose_dealer")}</h2>
              <p className="text-gray-500 text-xs mt-0.5 mb-3">{t("dealer_sub")}</p>
              <div className="relative">
                <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400" />
                <input data-testid="dealer-search-input" value={dealerQ}
                  onChange={(e) => { setDealerQ(e.target.value); loadDealers(e.target.value); }}
                  placeholder={t("search_dealer")} className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl outline-none focus:border-[#8B0000]" />
              </div>
              <div className="mt-3 flex-1 overflow-y-auto scrollbar-thin space-y-2 max-h-[38vh]">
                {dealers.map((d) => (
                  <button key={d.id} data-testid={`dealer-option-${d.id}`} onClick={() => setDealer(d)}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all ${dealer?.id === d.id ? "border-[#8B0000] bg-[#FDF2F2]" : "border-gray-200 hover:border-gray-300"}`}>
                    <div className="font-semibold text-gray-900 text-sm">{d.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{d.city}, {d.state} {d.pin && `· ${d.pin}`}</div>
                  </button>
                ))}
                {dealers.length === 0 && <p className="text-center text-sm text-gray-400 py-6">No dealers found</p>}
              </div>
              <button data-testid="dealer-confirm-btn" disabled={!dealer || busy} onClick={doRedeem}
                className="mt-3 w-full bg-[#8B0000] hover:bg-[#6B0000] text-white font-extrabold text-lg py-4 rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />} {t("confirm_earn")}
              </button>
            </div>
          )}

          {step === "upi" && result && (
            <div className="animate-slide-up flex-1 flex flex-col">
              <h2 className="font-display text-xl font-bold text-gray-900">UPI Cash Payout</h2>
              <p className="text-sm text-gray-500 mt-1">Wallet balance: <b>{result.wallet_balance} pts</b> (₹{result.wallet_balance})</p>
              <div className="mt-5 space-y-3">
                <input data-testid="upi-id-input" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="UPI ID (e.g. name@okaxis)"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl outline-none focus:border-[#8B0000]" />
                <input data-testid="upi-holder-input" value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Account holder name"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl outline-none focus:border-[#8B0000]" />
                <label className="flex items-start gap-2 text-sm text-gray-600">
                  <input data-testid="upi-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#8B0000]" />
                  <span>{t("consent_text")}</span>
                </label>
              </div>
              <button data-testid="upi-submit-btn" disabled={!upiId || !holder || busy} onClick={doUpi}
                className="mt-auto w-full bg-[#8B0000] hover:bg-[#6B0000] text-white font-extrabold text-lg py-4 rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />} Send to UPI
              </button>
            </div>
          )}

          {step === "gift" && (
            <div className="animate-slide-up flex-1 flex flex-col">
              <h2 className="font-display text-xl font-bold text-gray-900">Choose a Gift</h2>
              <p className="text-sm text-gray-500 mt-1">Balance: <b>{result?.wallet_balance} pts</b></p>
              <div className="mt-4 grid grid-cols-2 gap-3 overflow-y-auto scrollbar-thin max-h-[34vh]">
                {gifts.map((g) => (
                  <button key={g.id} data-testid={`gift-option-${g.id}`} onClick={() => setGift(g)}
                    className={`text-left border-2 rounded-xl overflow-hidden ${gift?.id === g.id ? "border-[#8B0000]" : "border-gray-200"}`}>
                    <img src={g.image} alt={g.name} className="h-20 w-full object-cover" />
                    <div className="p-2">
                      <div className="text-xs font-semibold text-gray-900 line-clamp-1">{g.name}</div>
                      <div className="text-xs text-[#8B0000] font-bold">{g.points_cost} pts</div>
                    </div>
                  </button>
                ))}
              </div>
              <input data-testid="gift-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Delivery PIN code"
                className="mt-3 w-full px-4 py-3 border-2 border-gray-300 rounded-xl outline-none focus:border-[#8B0000]" />
              <input data-testid="gift-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery address"
                className="mt-2 w-full px-4 py-3 border-2 border-gray-300 rounded-xl outline-none focus:border-[#8B0000]" />
              <button data-testid="gift-order-btn" disabled={!gift || !pin || !address || busy} onClick={doGiftOrder}
                className="mt-3 w-full bg-[#8B0000] hover:bg-[#6B0000] text-white font-extrabold text-lg py-4 rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-5 w-5 animate-spin" />} Redeem Gift
              </button>
            </div>
          )}

          {step === "done" && result && (
            <div className="animate-slide-up flex-1 flex flex-col items-center justify-center text-center" data-testid="journey-success">
              <div className="animate-pop h-24 w-24 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-14 w-14 text-green-600" />
              </div>
              <h2 className="mt-6 font-display text-3xl font-extrabold text-gray-900">{t("success")}</h2>
              {rewardType === "wallet" && <p className="mt-2 text-gray-600"><b className="text-[#8B0000]">{result.reward_points}</b> {t("points_added")}</p>}
              {rewardType === "upi" && upiResult && (upiResult.below_threshold
                ? <p className="mt-2 text-amber-700 text-sm max-w-xs">{upiResult.message}</p>
                : <p className="mt-2 text-gray-600">UPI payout <b>{upiResult.status}</b> · ₹{upiResult.amount_inr} <br /><span className="font-mono text-xs">{upiResult.provider_reference}</span></p>)}
              {rewardType === "gift" && upiResult?.order && <p className="mt-2 text-gray-600">Order <b>{upiResult.order.status}</b> · {upiResult.order.gift_name}<br /><span className="font-mono text-xs">Ref {upiResult.order.id.slice(0, 8).toUpperCase()}</span></p>}
              <div className="mt-6 w-full bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-xs uppercase tracking-wider text-gray-400">{t("new_balance")}</p>
                <p className="kpi-num text-3xl text-gray-900">{(upiResult?.balance ?? result.wallet_balance)} <span className="text-lg text-gray-400">pts</span></p>
              </div>
              <p className="mt-3 text-xs text-gray-400">{t("reference")}: <span className="font-mono">{result.reference}</span></p>
              <button data-testid="journey-done-btn" onClick={() => { setStep("code"); setCode(""); setCoupon(null); setResult(null); setUpiResult(null); setDealer(null); setMobile(""); }}
                className="mt-8 w-full border-2 border-[#8B0000] text-[#8B0000] hover:bg-[#FDF2F2] font-bold py-3.5 rounded-xl">{t("done")}</button>
            </div>
          )}
        </div>
        <footer className="px-4 py-3 border-t border-gray-100 text-center"><PoweredBy /></footer>
      </div>
    </div>
  );
}
